import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getTaskDetails } from '../../../../redux/Slices/TasksSlice';

// view tasks component

const ViewTasks = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(getTaskDetails(id)).unwrap();
        const details = payload?.data || payload;
        setTask(details);
      } catch (error) {
        toast.error(error || t('tasks.view.messages.fetchError'), { rtl: isRTL });
        navigate('/tasks');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate]);


  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-US');
    } catch {
      return value;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: t('tasks.statuses.pending'),
      in_progress: t('tasks.statuses.in_progress'),
      completed: t('tasks.statuses.completed'),
      on_hold: t('tasks.statuses.on_hold'),
      cancelled: t('tasks.statuses.cancelled'),
    };
    return statusMap[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const priorityMap = {
      low: t('tasks.priorities.low'),
      medium: t('tasks.priorities.medium'),
      high: t('tasks.priorities.high'),
    };
    return priorityMap[priority] || priority;
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={cardStyle}>{t('tasks.view.loading')}</div>
      </div>
    );
  }

  if (!task) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={cardStyle}>{t('tasks.view.notFound')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          {isRTL ? (
            <>
              <h2 style={{ margin: 0, fontSize: '20px' }}>{t('tasks.view.title')}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => navigate('/tasks')}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid #444',
                    borderRadius: 6,
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {t('tasks.view.back')}
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ margin: 0, fontSize: '20px' }}>{t('tasks.view.title')}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => navigate('/tasks')}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid #444',
                    borderRadius: 6,
                    padding: '8px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {t('tasks.view.back')}
                </button>
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={gridStyle}>
            <DetailItem label={t('tasks.view.fields.taskNameAr')} value={task.name_ar || '—'} isRTL={isRTL} />
            <DetailItem label={t('tasks.view.fields.taskNameEn')} value={task.name_en || '—'} isRTL={isRTL} />
          </div>

          <DetailItem 
            label={t('tasks.view.fields.project')} 
            value={isRTL 
              ? (task.project?.name_ar || task.project?.name_en || '—')
              : (task.project?.name_en || task.project?.name_ar || '—')} 
            isRTL={isRTL}
          />
          <DetailItem 
            label={t('tasks.add.employee')} 
            value={task.employee?.name || (isRTL
              ? (task.employee?.name_ar || task.employee?.name_en || '—')
              : (task.employee?.name_en || task.employee?.name_ar || '—'))} 
            isRTL={isRTL}
          />
          <DetailItem label={t('tasks.view.fields.status')} value={getStatusLabel(task.status)} isRTL={isRTL} />
          <DetailItem label={t('tasks.view.fields.priority')} value={getPriorityLabel(task.priority)} isRTL={isRTL} />
          <DetailItem label={t('tasks.view.fields.progress')} value={task.progress ? `${task.progress}%` : '—'} isRTL={isRTL} />

          <div style={gridStyle}>
            <DetailItem label={t('tasks.view.fields.startDate')} value={formatDate(task.start_date)} isRTL={isRTL} />
            <DetailItem label={t('tasks.view.fields.endDate')} value={formatDate(task.end_date)} isRTL={isRTL} />
          </div>

          <DetailItem label={t('tasks.view.fields.description')} value={task.description || '—'} isRTL={isRTL} />
          <DetailItem label={t('tasks.view.fields.notes')} value={task.notes || '—'} isRTL={isRTL} />
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value, isRTL = true }) => (
  <div style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, color: 'white' }}>{value}</div>
  </div>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 800,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 16,
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  width: '100%',
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

const modalSecondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

export default ViewTasks;

